"use client";
import dynamic from "next/dynamic";
import React from "react";
import { Skeleton } from "antd";
import MyCard from "@/app/components/MyCard";
import type { ISignalDetails } from "..";

// Dynamic imports for the components
const PivotPoints = dynamic(() => import("./PivotPoint"), {
  loading: () => <Skeleton active />,
});
const Oscillators = dynamic(
  () => import("@/app/components/products/ProductSignals/Details/TechnicalAnalysis/Oscillators"),
  {
    loading: () => <Skeleton active />,
  },
);
const TechnicalScore = dynamic(
  () => import("@/app/components/products/ProductSignals/Details/TechnicalAnalysis/TechnicalScore"),
  {
    loading: () => <Skeleton active />,
  },
);
const MovingAverage = dynamic(
  () => import("@/app/components/products/ProductSignals/Details/TechnicalAnalysis/MovingAverage"),
  {
    loading: () => <Skeleton active />,
  },
);
const MovingAveragesTable = dynamic(
  () => import("@/app/components/products/ProductSignals/Details/TechnicalAnalysis/MovingAveragesTable"),
  {
    loading: () => <Skeleton active />,
  },
);
const HistoricalTechnicalAnalysis = dynamic(
  () => import("@/app/components/products/ProductSignals/Details/TechnicalAnalysis/HistoricalTechnicalAnalysis"),
  {
    loading: () => <Skeleton active />,
  },
);

// Summary status function remains unchanged
export const summaryStatus = (buy: number, sell: number) => {
  let status = "Neutral";
  if (buy > sell) {
    if (buy > 2 * sell) {
      status = "Strong Buy";
    } else {
      status = "Buy";
    }
  } else if (buy < sell) {
    if (sell > 2 * buy) {
      status = "Strong Sell";
    } else {
      status = "Sell";
    }
  }
  return status;
};

export default function TechnicalAnalysis({
  data,
  symbol,
  loading,
  duration,
}: {
  data?: ISignalDetails;
  loading: boolean;
  symbol: string;
  duration: string;
}) {
  return (
    <MyCard>
      <div className={"flex flex-col gap-6"}>
        <HistoricalTechnicalAnalysis symbol={symbol} selectedDuration={duration} />
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <MovingAverage data={data?.text_summary} loading={loading} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <TechnicalScore data={data} loading={loading} symbol={symbol} />
          </div>
        </div>
        <PivotPoints data={data?.details?.pivot_data} loading={loading} />
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <Oscillators data={data?.details?.oscillators_summary} loading={loading} date={data?.details?.created_at} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <MovingAveragesTable
              data={data?.details?.moving_average_data}
              loading={loading}
              date={data?.details?.created_at}
            />
          </div>
        </div>
      </div>
    </MyCard>
  );
}
